import { useEffect, useMemo, useState } from "react";
import { Share2, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "athr:ios-install-prompt:dismiss-until";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

const isIosSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/i.test(ua);
  return isIos && isSafari;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
};

export function IOSInstallPrompt() {
  const [open, setOpen] = useState(false);

  const shouldShow = useMemo(() => {
    if (typeof window === "undefined") return false;
    const dismissedUntil = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil > Date.now()) return false;
    return isIosSafari() && !isStandalone();
  }, []);

  useEffect(() => {
    setOpen(shouldShow);
  }, [shouldShow]);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] border-t border-slate-200/50 bg-white/90 backdrop-blur-xl p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
      <div className="mx-auto flex max-w-3xl items-start justify-between gap-3 rounded-[1.25rem] border border-slate-200/60 bg-white/75 p-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">Install Athr on your iPhone</p>
          <p className="mt-1 text-xs text-slate-600">
            Tap <span className="inline-flex items-center gap-1 font-medium text-slate-800"><Share2 className="h-3 w-3" /> Share</span> then choose <span className="font-medium text-slate-800">Add to Home Screen</span>.
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 rounded-full" onClick={dismiss} aria-label="Dismiss iOS install prompt">
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

